import React, { useState, useRef, useEffect } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import LinearProgress from "@mui/material/LinearProgress";
import { Close as CloseIcon, CheckCircle as CheckCircleIcon, UploadFile as UploadFileIcon } from "@mui/icons-material";
import { AiOutlineInfoCircle } from "react-icons/ai";
import Tooltip from "@mui/material/Tooltip";
import "../pubvision/AssetsGeneration.css";
import PropTypes from "prop-types";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const PresentationDialog = ({
  open,
  onClose,
  slidesCount,
  setSlidesCount,
  presentationLanguage,
  setPresentationLanguage,
  speakerNotes,
  setSpeakerNotes,
  contentLevel,
  setContentLevel,
  presentationSelectedColor,
  setPresentationSelectedColor,
  presentationCustomColor,
  setPresentationCustomColor,
  onStartGeneratingPresentation,
  publicationId,
  selectedSections,
  setSelectedSections,
  preloadedSections,   // if provided: skip Step 1 and show these sections in Step 2
  preloadedPptId,      // pptId matching the preloaded sections
  setActiveTasks,
  setTaskStatus,
  setRefreshTrigger,
}) => {
  // ── Local state ──────────────────────────────────────────────────────────────
  const [sections, setSections] = useState([]);
  const [loadingSections, setLoadingSections] = useState(false);
  const [sectionsError, setSectionsError] = useState(null);
  const [sectionsLoaded, setSectionsLoaded] = useState(false); // true after step-1 completes
  const [progress, setProgress] = useState(0);
  const [progressMessages, setProgressMessages] = useState([]);
  const [fetchedPptId, setFetchedPptId] = useState(null); // pptId from step-1
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoName, setLogoName] = useState("");
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const wsRef = useRef(null);

  // Reset all state every time the dialog opens
  // If preloadedSections + preloadedPptId are provided, jump directly to Step 2
  useEffect(() => {
    if (open) {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      setProgress(0);
      setProgressMessages([]);
      setSectionsError(null);
      setLoadingSections(false);

      if (preloadedSections && preloadedSections.length > 0 && preloadedPptId) {
        // Jump straight to Step 2 with existing sections
        setSections(preloadedSections);
        setSelectedSections(
          preloadedSections.map((section) => ({
            section_id: section.section_id,
            section_name: section.section_name,
            ppt_template_ids:
              section.templates?.[0]?.ppt_template_id ||
              section.llm_recommendation?.[0]?.ppt_template_id ||
              [],
          }))
        );
        setFetchedPptId(preloadedPptId);
        setSectionsLoaded(true);
      } else {
        // Fresh start: Step 1
        setSections([]);
        setSectionsLoaded(false);
        setFetchedPptId(null);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const CREDITS_REQUIRED = 4;

  // ── Progress messaging ───────────────────────────────────────────────────────
  const progressSteps = [
    { threshold: 0,  text: "Processing your publication…" },
    { threshold: 10, text: "Detecting document structure and section boundaries…" },
    { threshold: 20, text: "Extracting the abstract and key metadata…" },
    { threshold: 30, text: "Parsing the introduction to understand research context…" },
    { threshold: 40, text: "Analyzing study design and methodology…" },
    { threshold: 50, text: "Extracting results, figures, and statistical findings…" },
    { threshold: 60, text: "Reviewing discussion and clinical implications…" },
    { threshold: 70, text: "Indexing references, tables, and supplementary material…" },
    { threshold: 80, text: "Structuring insights for intelligent search and retrieval…" },
    { threshold: 90, text: "Preparing the publication for interactive analysis…" },
  ];

  const updateProgressMessages = (currentProgress) => {
    const newMessages = [];
    progressSteps.forEach((step) => {
      if (currentProgress >= step.threshold) {
        newMessages.push({
          text: step.text,
          completed: currentProgress > step.threshold + 5,
        });
      }
    });
    if (newMessages.length > 0 && currentProgress < 100) {
      newMessages.at(-1).completed = false;
    }
    setProgressMessages(newMessages);
  };

  // ── Reset dialog state when it closes ────────────────────────────────────────
  const handleClose = () => {
    // Close any open WebSocket
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    // Pass the fetched pptId and whether sections are loaded to the parent's onClose callback so it can sync state on close/cancel
    onClose(fetchedPptId, sectionsLoaded);

    // Reset step back to 1 for next open
    setSections([]);
    setSectionsLoaded(false);
    setSectionsError(null);
    setLoadingSections(false);
    setProgress(0);
    setProgressMessages([]);
    setFetchedPptId(null);
  };

  // ── STEP 1 action: called when user clicks "Fetch Sections" ──────────────────
  const handleFetchSections = async () => {
    const token = localStorage.getItem("authToken");
    setSectionsError(null);
    setLoadingSections(true);
    setProgress(0);
    setProgressMessages([]);

    try {
      // Call /get_templates_by_llm with publicationId, length, and professionType
      console.log("[PresentationDialog] Calling get_templates_by_llm with length:", slidesCount, "professionType:", contentLevel);
      const response = await fetch(
        `${BASE_URL}/get_templates_by_llm?publicationId=${publicationId}&length=${slidesCount}&professionType=${contentLevel}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to initiate section extraction");
      }

      const data = await response.json();
      console.log("[PresentationDialog] Received task_id:", data.task_id, "pptId:", data.pptId);

      if (!data.task_id) {
        throw new Error("No task_id received");
      }

      // Store the pptId for the templates fetch in step 2
      if (data.pptId) {
        setFetchedPptId(data.pptId);
        
        if (setActiveTasks && setTaskStatus) {
          setActiveTasks((prev) => {
            const nextArr = [...(prev.ppt || [])];
            if (!nextArr.includes(data.pptId)) nextArr.push(data.pptId);
            return { ...prev, ppt: nextArr };
          });
          localStorage.setItem("activeTasks", "true");
          setTaskStatus((prev) => ({
            ...prev,
            ppt: {
              ...(prev.ppt || {}),
              [data.pptId]: {
                progress: 0,
                status: "queued",
                publicationId: Number(publicationId),
                pptId: data.pptId,
              },
            },
          }));
          if (setRefreshTrigger) {
            setRefreshTrigger(prev => prev + 1);
          }
        }
      }

      // Setup WebSocket to track progress
      const wsBaseUrl = process.env.REACT_APP_WS_BASE_URL;
      const wsUrl = `${wsBaseUrl}/taskStatus/${data.task_id}`;
      console.log("[PresentationDialog] Connecting to WebSocket:", wsUrl);

      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log("[PresentationDialog] WebSocket connected");
      };

      ws.onmessage = async (event) => {
        const wsData = JSON.parse(event.data);
        console.log("[PresentationDialog] WebSocket message:", wsData);

        const currentProgress = wsData.progress || 0;
        setProgress(currentProgress);
        updateProgressMessages(currentProgress);

        if (currentProgress >= 100) {
          console.log("[PresentationDialog] Progress complete, fetching sections...");
          setProgressMessages((prev) => prev.map((msg) => ({ ...msg, completed: true })));
          ws.close();

          // Fetch the actual sections/templates
          try {
            const templatesResponse = await fetch(
              `${BASE_URL}/get_ppt_templates?pptId=${fetchedPptId || data.pptId}`,
              {
                method: "GET",
                headers: {
                  Authorization: `Bearer ${token}`,
                  "Content-Type": "application/json",
                },
              }
            );

            if (!templatesResponse.ok) {
              throw new Error("Failed to fetch presentation templates");
            }

            const templatesData = await templatesResponse.json();
            console.log("[PresentationDialog] Templates received:", templatesData);

            if (Array.isArray(templatesData)) {
              setSections(templatesData);
              // Auto-select all sections by default
              setSelectedSections(
                templatesData.map((section) => ({
                  section_id: section.section_id,
                  section_name: section.section_name,
                  ppt_template_ids:
                    section.templates?.[0]?.ppt_template_id ||
                    section.llm_recommendation?.[0]?.ppt_template_id ||
                    [],
                }))
              );
            }

            setLoadingSections(false);
            setSectionsLoaded(true); // ← advance to step 2
          } catch (error) {
            console.error("[PresentationDialog] Error fetching templates:", error);
            setSectionsError("Failed to load presentation templates");
            setLoadingSections(false);
          }
        }

        if (wsData.error || currentProgress < 0) {
          console.error("[PresentationDialog] WebSocket error:", wsData.error);
          setSectionsError("An error occurred while extracting sections");
          setLoadingSections(false);
          ws.close();
        }
      };

      ws.onerror = (error) => {
        console.error("[PresentationDialog] WebSocket error:", error);
        setSectionsError("Connection error. Please try again.");
        setLoadingSections(false);
      };

      ws.onclose = () => {
        console.log("[PresentationDialog] WebSocket closed");
      };
    } catch (error) {
      console.error("[PresentationDialog] Error:", error);
      setSectionsError("Failed to initialize section extraction");
      setLoadingSections(false);
    }
  };

  // ── Section helpers ───────────────────────────────────────────────────────────
  const handleSectionChange = (section) => {
    setSelectedSections((prev) => {
      const isSelected = prev.some((s) => s.section_id === section.section_id);
      if (isSelected) {
        return prev.filter((s) => s.section_id !== section.section_id);
      } else {
        return [
          ...prev,
          {
            section_id: section.section_id,
            section_name: section.section_name,
            ppt_template_ids:
              section.templates?.[0]?.ppt_template_id ||
              section.llm_recommendation?.[0]?.ppt_template_id ||
              [],
          },
        ];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedSections.length === sections.length) {
      setSelectedSections([]);
    } else {
      setSelectedSections(
        sections.map((section) => ({
          section_id: section.section_id,
          section_name: section.section_name,
          ppt_template_ids:
            section.templates?.[0]?.ppt_template_id ||
            section.llm_recommendation?.[0]?.ppt_template_id ||
            [],
        }))
      );
    }
  };

  const isSectionSelected = (section) =>
    selectedSections.some((s) => s.section_id === section.section_id);

  // ── STEP 2 action: called when user clicks "Start Generating" ────────────────
  const handleGenerateClick = () => {
    console.log("[PresentationDialog] Start Generating clicked.");
    console.log("[PresentationDialog] Selected sections:", selectedSections);
    console.log("[PresentationDialog] fetchedPptId:", fetchedPptId);
    onStartGeneratingPresentation(fetchedPptId);
  };

  // ── Content level data ────────────────────────────────────────────────────────
  const levels = [
    "Physicians",
    "Medical Affairs",
    "Clinical Development",
    "Commercial Teams",
    "Market Access & HEOR",
    "Regulatory & Safety",
    "KOLs / Researchers",
    "Executives",
    "Patients"
  ];

  const levelDescriptions = {
    "Physicians": "Frontline clinicians focused on patient care, diagnosis, treatment decisions, and clinical outcomes.",
    "Medical Affairs": "Bridge between clinical data and the medical community — managing publications, advisory boards, and scientific exchange.",
    "Clinical Development": "Designs and runs clinical trials to evaluate safety, efficacy, and dosing of new therapies.",
    "Commercial Teams": "Drive product adoption through sales strategy, marketing, and customer engagement with healthcare providers.",
    "Market Access & HEOR": "Secure payer coverage and demonstrate value through health economics, outcomes research, and reimbursement strategy.",
    "Regulatory & Safety": "Ensure compliance with regulatory requirements, manage drug safety reporting, and oversee pharmacovigilance.",
    "KOLs / Researchers": "Academic leaders and researchers who shape clinical practice through publications, guidelines, and thought leadership.",
    "Executives": "Senior leadership driving corporate strategy, pipeline decisions, and organizational priorities.",
    "Patients": "Individuals receiving medical care, seeking to understand disease states, treatments, and clinical outcomes."
  };

  // ── Slide-length options ──────────────────────────────────────────────────────
  const slideOptions = [
    { value: "short", label: "Short", desc: "5–7 slides" },
    { value: "medium", label: "Medium", desc: "12–15 slides" },
    { value: "detailed", label: "Detailed", desc: "16–25 slides" },
  ];

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      className="abstract-video-dialog"
      sx={{
        "& .MuiBackdrop-root": { backgroundColor: "rgba(0, 0, 0, 0.5)" },
        "& .MuiPaper-root": { fontFamily: "'Inter', sans-serif" },
        "& *": { fontFamily: "'Inter', sans-serif !important" },
      }}
    >
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <DialogTitle className="dialog-title">
        <IconButton
          aria-label="close"
          onClick={handleClose}
          className="close-button"
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogTitle className="dialog-title main-title">
        <strong>Presentation : Enter requirements below</strong>
      </DialogTitle>
      <div className="dialog-subtitle-container">
        <p className="dialog-subtitle">
          Please fill the form below to get the presentation as per your requirement
        </p>
      </div>

      <DialogContent className="dialog-content">

        {/* ══════════════════════════════════════════════════════════════════════
            STEP 1 — shown when sections have NOT been loaded yet
        ══════════════════════════════════════════════════════════════════════ */}
        {!sectionsLoaded && (
          <>
            {!loadingSections && (
              <>
            {/* Choose Content Level */}
            <div className="dialog-field">
              <FormControl component="fieldset">
                <FormLabel component="legend" className="dialog-label" focused={false}>
                  Choose Presentation Content Understanding Level
                </FormLabel>
                <Select
                  value={contentLevel}
                  onChange={(e) => {
                    console.log("[PresentationDialog] contentLevel:", e.target.value);
                    setContentLevel(e.target.value);
                  }}
                  displayEmpty
                  className="level-options"
                  style={{ marginTop: "12px", width: "100%" }}
                  disabled={loadingSections}
                >
                  {levels.map((level) => (
                    <MenuItem key={level} value={level} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span>{level}</span>
                      <Tooltip
                        title={levelDescriptions[level]}
                        arrow
                        placement="right"
                        slotProps={{
                          tooltip: {
                            sx: {
                              backgroundColor: "#fff",
                              color: "#1E4DE3",
                              fontSize: "13px",
                              lineHeight: "1.5",
                              padding: "10px 14px",
                              maxWidth: "350px",
                              border: "1px solid #1E4DE3",
                              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                              "& .MuiTooltip-arrow": {
                                color: "#fff",
                                "&::before": { border: "1px solid #1E4DE3" },
                              },
                            },
                          },
                        }}
                      >
                        <IconButton
                          size="small"
                          style={{ padding: "4px", color: "#9ca3af", transition: "all 0.2s ease" }}
                          onMouseEnter={(e) => { e.currentTarget.style.transform = "scale(1.1)"; }}
                          onMouseLeave={(e) => { e.currentTarget.style.transform = "scale(1)"; }}
                        >
                          <AiOutlineInfoCircle size={16} />
                        </IconButton>
                      </Tooltip>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>

            {/* Choose Presentation Length */}
            <div className="dialog-field" style={{ marginTop: "24px" }}>
              <FormLabel component="legend" className="dialog-label" focused={false} style={{ marginBottom: "12px" }}>
                Choose Presentation Length
              </FormLabel>
              <div style={{ display: "flex", gap: "16px", width: "100%" }}>
                {slideOptions.map((opt) => {
                  const isSelected = slidesCount === opt.value;
                  return (
                    <div
                      key={opt.value}
                      onClick={() => {
                        console.log("[PresentationDialog] slidesCount:", opt.value);
                        setSlidesCount(opt.value);
                      }}
                      style={{
                        flex: 1,
                        padding: "16px",
                        border: isSelected ? "2px solid #1E4DE3" : "1px solid #e0e0e0",
                        borderRadius: "12px",
                        backgroundColor: isSelected ? "#f0f4ff" : "#fff",
                        cursor: "pointer",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        transition: "all 0.2s ease",
                        boxShadow: isSelected ? "0 4px 12px rgba(30, 77, 227, 0.15)" : "none",
                        transform: isSelected ? "translateY(-2px)" : "none",
                      }}
                      onMouseEnter={(e) => {
                        if (!isSelected) {
                          e.currentTarget.style.borderColor = "#b3c4f7";
                          e.currentTarget.style.backgroundColor = "#f8faff";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isSelected) {
                          e.currentTarget.style.borderColor = "#e0e0e0";
                          e.currentTarget.style.backgroundColor = "#fff";
                        }
                      }}
                    >
                      <span style={{ 
                        fontSize: "16px", 
                        fontWeight: isSelected ? 700 : 500, 
                        color: isSelected ? "#1E4DE3" : "#333",
                        marginBottom: "4px"
                      }}>
                        {opt.label}
                      </span>
                      <span style={{ 
                        fontSize: "13px", 
                        color: isSelected ? "#1E4DE3" : "#666",
                        fontWeight: 400
                      }}>
                        {opt.desc}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
            </>
            )}

            {/* Loading / error feedback while fetching sections */}
            {loadingSections && (
              <>
                <div
                  style={{
                    fontSize: "13px",
                    color: "#666",
                    fontStyle: "italic",
                    marginTop: "8px",
                    marginBottom: "8px",
                  }}
                >
                  Note: Takes approx 4 min. You can safely close window and come back.
                </div>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    padding: "24px",
                    backgroundColor: "#f8f9fa",
                    borderRadius: "12px",
                    marginTop: "10px",
                    gap: "16px",
                  }}
                >
                  {/* Progress bar */}
                  <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: "10px" }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
                      <span style={{ color: "#1E4DE3", fontSize: "15px", fontWeight: 600 }}>
                        Extracting Sections
                      </span>
                      <span style={{ color: "#1E4DE3", fontSize: "15px", fontWeight: 700 }}>
                        {progress}%
                      </span>
                    </div>
                    <LinearProgress
                      variant="determinate"
                      value={progress}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        backgroundColor: "#e0e0e0",
                        "& .MuiLinearProgress-bar": { backgroundColor: "#1E4DE3", borderRadius: 4 },
                      }}
                    />
                  </div>

                  {/* Step messages */}
                  {progressMessages.length > 0 && (
                    <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: "8px", marginTop: "8px" }}>
                      {progressMessages.map((msg, index) => (
                        <div key={index} style={{ display: "flex", alignItems: "flex-start", gap: "10px", padding: "8px 0" }}>
                          {msg.completed ? (
                            <CheckCircleIcon sx={{ color: "#1E4DE3", fontSize: "20px", marginTop: "2px", flexShrink: 0 }} />
                          ) : (
                            <CircularProgress size={16} sx={{ color: "#1E4DE3", marginTop: "2px", marginLeft: "2px", marginRight: "2px", flexShrink: 0 }} />
                          )}
                          <span style={{ color: msg.completed ? "#1E4DE3" : "#555", fontSize: "13px", lineHeight: "1.5", fontWeight: msg.completed ? 400 : 500 }}>
                            {msg.text}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Error state */}
            {sectionsError && (
              <div style={{ color: "#d32f2f", padding: "15px", backgroundColor: "#ffebee", borderRadius: "8px", marginTop: "10px", fontSize: "14px" }}>
                {sectionsError}
              </div>
            )}
          </>
        )}

        {/* ══════════════════════════════════════════════════════════════════════
            STEP 2 — shown after sections have been loaded
        ══════════════════════════════════════════════════════════════════════ */}
        {sectionsLoaded && (
          <>


            {/* Choose Sections */}
            <div className="dialog-field">
              <FormControl component="fieldset" fullWidth>
                <FormLabel component="legend" className="dialog-label" focused={false}>
                  Choose Sections
                </FormLabel>
                <div style={{ marginTop: "10px" }}>
                  {sections.length > 0 && (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedSections.length === sections.length}
                          indeterminate={selectedSections.length > 0 && selectedSections.length < sections.length}
                          onChange={handleSelectAll}
                          sx={{
                            color: "#1E4DE3",
                            "&.Mui-checked": { color: "#1E4DE3" },
                            "&.MuiCheckbox-indeterminate": { color: "#1E4DE3" },
                          }}
                        />
                      }
                      label="Select All"
                      sx={{ marginBottom: "8px", "& .MuiFormControlLabel-label": { fontWeight: 600, fontSize: "14px", color: "#333" } }}
                    />
                  )}
                  <div style={{ display: "flex", flexDirection: "column", gap: "4px", maxHeight: "200px", overflowY: "auto", padding: "4px" }}>
                    {sections.map((section) => {
                      const isSelected = isSectionSelected(section);
                      return (
                        <FormControlLabel
                          key={section.section_id}
                          control={
                            <Checkbox
                              checked={isSelected}
                              onChange={() => handleSectionChange(section)}
                              sx={{ color: "#1E4DE3", "&.Mui-checked": { color: "#1E4DE3" } }}
                            />
                          }
                          label={section.section_name}
                          sx={{ "& .MuiFormControlLabel-label": { fontSize: "14px", color: "#555" } }}
                        />
                      );
                    })}
                  </div>
                  {sections.length === 0 && (
                    <div style={{ color: "#888", padding: "20px", textAlign: "center", backgroundColor: "#f8f9fa", borderRadius: "8px", fontSize: "14px" }}>
                      No sections available
                    </div>
                  )}
                </div>
              </FormControl>
            </div>
          </>
        )}

        {!sectionsLoaded && !loadingSections && (
          <>
        {/* Color Selection */}
        <div className="dialog-field" style={{ marginTop: "24px", width: "100%" }}>
          <FormLabel component="legend" className="dialog-label" focused={false} style={{ marginBottom: "8px" }}>
            Choose Primary Color
          </FormLabel>
          <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
            {[
              { main: "#1E4DE3", light: "#B3C8FF" },
              { main: "#16A34A", light: "#A7F3D0" },
              { main: "#F97316", light: "#FDBA74" },
              { main: "#9333EA", light: "#D8B4FE" },
              { main: "#EAB308", light: "#FDE047" },
            ].map((colorObj) => {
              const color = colorObj.main;
              return (
              <div
                key={color}
                onClick={() => setPresentationSelectedColor(presentationSelectedColor === color ? "" : color)}
                style={{
                  width: "32px",
                  height: "20px",
                  borderRadius: "4px",
                  display: "flex",
                  cursor: "pointer",
                  border: presentationSelectedColor === color ? "2px solid #fff" : "2px solid transparent",
                  boxShadow: presentationSelectedColor === color ? "0 0 0 2px #000" : "none",
                  overflow: "hidden",
                  transition: "all 0.2s"
                }}
              >
                <div style={{ flex: 1, backgroundColor: colorObj.main }}></div>
                <div style={{ flex: 1, backgroundColor: colorObj.light }}></div>
              </div>
            )})}
          </div>
        </div>

        {/* Upload Logo */}
        <div className="dialog-field" style={{ marginTop: "24px", width: "100%", marginBottom: "16px" }}>
          <FormLabel component="legend" className="dialog-label" focused={false} style={{ marginBottom: "8px" }}>
            Upload Logo
          </FormLabel>
          <input 
            type="file" 
            id="presentation-logo-upload" 
            accept="image/*" 
            style={{ display: "none" }}
            onChange={(e) => {
               if (e.target.files && e.target.files[0]) {
                 const file = e.target.files[0];
                 setLogoName(file.name);
                 const reader = new FileReader();
                 reader.onload = (event) => {
                   setLogoPreview(event.target.result);
                 };
                 reader.readAsDataURL(file);
               }
            }}
          />
          {logoPreview ? (
            <div style={{
              border: "1px solid #e2e8f0",
              borderRadius: "8px",
              padding: "12px 16px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              backgroundColor: "#fff",
              boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
              minHeight: "74px",
              boxSizing: "border-box",
              cursor: "pointer",
              transition: "border-color 0.2s"
            }}
            onMouseOver={(e) => e.currentTarget.style.borderColor = "#1E4DE3"}
            onMouseOut={(e) => e.currentTarget.style.borderColor = "#e2e8f0"}
            onClick={() => setIsPreviewModalOpen(true)}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "16px", overflow: "hidden" }}>
                <div style={{
                  width: "48px",
                  height: "48px",
                  borderRadius: "6px",
                  backgroundColor: "#f8fafc",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: "1px solid #e2e8f0",
                  overflow: "hidden",
                  flexShrink: 0
                }}>
                  <img src={logoPreview} alt="Logo Preview" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                </div>
                <div style={{ display: "flex", flexDirection: "column", overflow: "hidden" }}>
                  <span style={{ fontSize: "14px", fontWeight: 500, color: "#1e293b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "250px" }}>
                    {logoName}
                  </span>
                  <span style={{ fontSize: "12px", color: "#64748b" }}>Logo Image</span>
                </div>
              </div>
              <IconButton
                size="small"
                style={{ backgroundColor: "#f1f5f9" }}
                onClick={(e) => {
                  e.stopPropagation();
                  setLogoPreview(null);
                  setLogoName("");
                  document.getElementById("presentation-logo-upload").value = "";
                }}
              >
                <CloseIcon style={{ fontSize: "16px", color: "#64748b" }} />
              </IconButton>
            </div>
          ) : (
            <div style={{
              position: "relative",
              border: "2px dashed #94a3b8",
              borderRadius: "12px",
              padding: "12px 16px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "12px",
              backgroundColor: "#f8fafc",
              cursor: "pointer",
              transition: "all 0.2s",
              minHeight: "74px",
              boxSizing: "border-box"
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.borderColor = "#1E4DE3";
              e.currentTarget.style.backgroundColor = "#eff6ff";
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.borderColor = "#94a3b8";
              e.currentTarget.style.backgroundColor = "#f8fafc";
            }}
            onClick={() => document.getElementById("presentation-logo-upload").click()}
            >
              <UploadFileIcon style={{ fontSize: "28px", color: "#64748b" }} />
              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                <span style={{ fontSize: "14px", fontWeight: 500, color: "#475569" }}>Click to upload logo image</span>
                <span style={{ fontSize: "12px", color: "#94a3b8", marginTop: "2px" }}>PNG, JPG, SVG up to 2MB</span>
              </div>
            </div>
          )}
        </div>

        {/* Logo Preview Modal */}
        <Dialog open={isPreviewModalOpen} onClose={() => setIsPreviewModalOpen(false)} maxWidth="md" fullWidth>
          <div style={{ position: "relative", padding: "24px", display: "flex", justifyContent: "center", backgroundColor: "#fff" }}>
            <IconButton
              style={{ position: "absolute", top: "12px", right: "12px", backgroundColor: "#f1f5f9" }}
              onClick={() => setIsPreviewModalOpen(false)}
            >
              <CloseIcon />
            </IconButton>
            <img src={logoPreview} alt="Logo Preview Full" style={{ maxWidth: "100%", maxHeight: "70vh", objectFit: "contain", borderRadius: "8px" }} />
          </div>
        </Dialog>

        {/* Credits */}
        <div style={{ marginTop: "16px", fontSize: "14px", color: "#1E4DE3" }}>
          Credits required: <strong>{CREDITS_REQUIRED}</strong>
        </div>
        </>
        )}
      </DialogContent>

      {/* ── Footer actions ──────────────────────────────────────────────────── */}
      <DialogActions className="dialog-actions">
        <Button onClick={handleClose} variant="text" className="cancel-btn">
          Cancel
        </Button>

        {/* STEP 1 button */}
        {!sectionsLoaded && (
          <Button
            onClick={handleFetchSections}
            variant="contained"
            className="start-generating-btn"
            disabled={loadingSections || !slidesCount}
            sx={{
              "&.Mui-disabled": {
                backgroundColor: "#ccc !important",
                color: "#888 !important",
                cursor: "not-allowed",
                pointerEvents: "none",
              },
            }}
          >
            {loadingSections ? "Fetching Sections…" : "Next →"}
          </Button>
        )}

        {/* STEP 2 button */}
        {sectionsLoaded && (
          <Button
            onClick={handleGenerateClick}
            variant="contained"
            className="start-generating-btn"
            disabled={selectedSections.length === 0}
            sx={{
              "&.Mui-disabled": {
                backgroundColor: "#ccc !important",
                color: "#888 !important",
                cursor: "not-allowed",
                pointerEvents: "none",
              },
            }}
          >
            Start Generating
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

PresentationDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  slidesCount: PropTypes.string.isRequired,
  setSlidesCount: PropTypes.func.isRequired,
  presentationLanguage: PropTypes.string.isRequired,
  setPresentationLanguage: PropTypes.func.isRequired,
  speakerNotes: PropTypes.string.isRequired,
  setSpeakerNotes: PropTypes.func.isRequired,
  contentLevel: PropTypes.string.isRequired,
  setContentLevel: PropTypes.func.isRequired,
  presentationSelectedColor: PropTypes.string.isRequired,
  setPresentationSelectedColor: PropTypes.func.isRequired,
  presentationCustomColor: PropTypes.string.isRequired,
  setPresentationCustomColor: PropTypes.func.isRequired,
  onStartGeneratingPresentation: PropTypes.func.isRequired,
  publicationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  selectedSections: PropTypes.array.isRequired,
  setSelectedSections: PropTypes.func.isRequired,
};

export default PresentationDialog;